import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createPendingOrder, hasPendingOrder, getCurrentUser } from '../utils/authManager';

export default function SubscriptionPage() {
  const [subscription, setSubscription] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState('alipay'); // alipay 或 wechat
  const [currentOrder, setCurrentOrder] = useState(null);
  const [showOrderInfo, setShowOrderInfo] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('subscription');
    if (stored) {
      setSubscription(JSON.parse(stored));
    }
  }, []);

  const plans = [
    {
      id: 'trial',
      name: '免费试用',
      price: 0,
      period: '7天',
      description: '体验全部精读功能',
      features: [
        '📖 完整精读训练',
        '🎧 听力练习',
        '🎙️ 口语跟读',
        '✍️ 翻译练习',
        '📝 摘要写作',
        '💎 金币奖励系统'
      ],
      popular: false,
      color: '#4caf50'
    },
    {
      id: 'monthly',
      name: '月度会员',
      price: 29,
      period: '月',
      description: '适合短期学习计划',
      features: [
        '📖 全部精读内容',
        '🎧 无限听力练习',
        '🎙️ AI口语评分',
        '✍️ AI翻译批改',
        '📝 摘要智能评分',
        '💎 双倍金币奖励',
        '📊 学习数据分析'
      ],
      popular: false,
      color: '#2196f3'
    },
    {
      id: 'quarterly',
      name: '季度会员',
      price: 79,
      period: '季',
      description: '最受欢迎的选择',
      originalPrice: 87,
      features: [
        '📖 全部精读内容',
        '🎧 无限听力练习',
        '🎙️ AI口语评分',
        '✍️ AI翻译批改',
        '📝 摘要智能评分',
        '💎 三倍金币奖励',
        '📊 学习数据分析',
        '👨‍🏫 专属学习顾问',
        '📱 优先技术支持'
      ],
      popular: true,
      color: '#ff9800'
    },
    {
      id: 'yearly',
      name: '年度会员',
      price: 239,
      period: '年',
      description: '最划算的选择',
      originalPrice: 348,
      features: [
        '📖 全部精读内容',
        '🎧 无限听力练习',
        '🎙️ AI口语评分',
        '✍️ AI翻译批改',
        '📝 摘要智能评分',
        '💎 五倍金币奖励',
        '📊 完整学习报告',
        '👨‍🏫 1对1学习顾问',
        '📱 7x24技术支持',
        '🎁 专属学习礼包',
        '🏆 年度学习勋章'
      ],
      popular: false,
      color: '#9c27b0'
    }
  ];

  const getExpiryDate = (planId) => {
    const now = new Date();
    switch (planId) {
      case 'trial':
        now.setDate(now.getDate() + 7);
        break;
      case 'monthly':
        now.setMonth(now.getMonth() + 1);
        break;
      case 'quarterly':
        now.setMonth(now.getMonth() + 3);
        break;
      case 'yearly':
        now.setFullYear(now.getFullYear() + 1);
        break;
      default:
        break;
    }
    return now.toISOString();
  };

  const handleSubscribe = (planId) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    // 免费试用直接订阅
    if (plan.price === 0) {
      const newSubscription = {
        planId: plan.id,
        planName: plan.name,
        price: plan.price,
        startDate: new Date().toISOString(),
        expiryDate: getExpiryDate(planId),
        status: 'active'
      };

      localStorage.setItem('subscription', JSON.stringify(newSubscription));
      setSubscription(newSubscription);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      return;
    }

    // 付费套餐显示支付二维码
    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  const handleCreateOrder = () => {
    if (!selectedPlan) return;
    
    const user = getCurrentUser();
    if (!user) {
      alert('请先登录');
      return;
    }

    // 检查是否已有待审核订单
    if (hasPendingOrder()) {
      alert('您已有一个待审核的订单，请等待审核完成后再提交新订单');
      return;
    }

    // 创建待审核订单
    const result = createPendingOrder(
      selectedPlan.id,
      selectedPlan.name,
      selectedPlan.price,
      selectedPayment
    );

    if (result.success) {
      setCurrentOrder(result);
      setShowOrderInfo(true);
    } else {
      alert(result.message);
    }
  };

  const handlePaymentSuccess = () => {
    if (!selectedPlan) return;

    const newSubscription = {
      planId: selectedPlan.id,
      planName: selectedPlan.name,
      price: selectedPlan.price,
      startDate: new Date().toISOString(),
      expiryDate: getExpiryDate(selectedPlan.id),
      status: 'active'
    };

    localStorage.setItem('subscription', JSON.stringify(newSubscription));
    setSubscription(newSubscription);
    setShowPaymentModal(false);
    setSelectedPlan(null);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysRemaining = () => {
    if (!subscription) return 0;
    const expiry = new Date(subscription.expiryDate);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  return (
    <div className="subscription-container">
      {/* 顶部导航栏 */}
      <header className="app-header">
        <div className="header-left">
          <div className="logo">
            <span className="logo-icon">✈️</span>
            <div>
              <h1>智伴精读</h1>
              <p className="logo-subtitle">AEN 五维一体 · 外刊精读</p>
            </div>
          </div>
        </div>
        <nav className="header-nav">
          <Link to="/" className="nav-link">首页</Link>
          <Link to="/subscription" className="nav-link active">订阅中心</Link>
          <Link to="/manager" className="nav-link">文章管理</Link>
          <div className="contact-info">
            <span className="contact-item">📞 187-8306-7878</span>
            <span className="contact-item">📧 aenedu.site@gmail.com</span>
          </div>
        </nav>
      </header>

      {/* 成功提示 */}
      {showSuccess && (
        <div className="success-toast">
          ✅ 订阅成功！{selectedPlan?.name || subscription?.planName}已激活
        </div>
      )}

      {/* 支付二维码弹窗 */}
      {showPaymentModal && selectedPlan && (
        <div className="payment-modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setShowPaymentModal(false)}>✕</button>
            
            <div className="payment-header">
              <h2>💳 支付 {selectedPlan.name}</h2>
              <p className="payment-amount">¥{selectedPlan.price} / {selectedPlan.period}</p>
            </div>

            {/* 支付方式选择 */}
            <div className="payment-tabs">
              <button 
                className={`payment-tab ${selectedPayment === 'alipay' ? 'active' : ''}`}
                onClick={() => setSelectedPayment('alipay')}
              >
                <span className="tab-icon">💙</span>
                支付宝
              </button>
              <button 
                className={`payment-tab ${selectedPayment === 'wechat' ? 'active' : ''}`}
                onClick={() => setSelectedPayment('wechat')}
              >
                <span className="tab-icon">💚</span>
                微信支付
              </button>
            </div>

            <div className="payment-methods">
              <div className="payment-method">
                {selectedPayment === 'alipay' ? (
                  <>
                    <div className="method-icon">💙</div>
                    <h3>支付宝支付</h3>
                    <div className="qrcode-container">
                      {/* 支付宝二维码 */}
                      <img 
                        src="/alipay-qrcode.png" 
                        alt="支付宝收款码"
                        style={{ 
                          width: '200px', 
                          height: '200px', 
                          borderRadius: '12px',
                          objectFit: 'cover',
                          border: '2px solid #e0e0e0'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="qrcode-placeholder" style={{ display: 'none' }}>
                        <div className="qrcode-icon">🔲</div>
                        <p>支付宝收款码</p>
                        <p className="qrcode-tip">请放置 alipay-qrcode.png</p>
                      </div>
                    </div>
                    <p className="payment-tip">使用支付宝扫一扫上方二维码完成支付</p>
                  </>
                ) : (
                  <>
                    <div className="method-icon">💚</div>
                    <h3>微信支付</h3>
                    <div className="qrcode-container">
                      {/* 微信二维码 */}
                      <img 
                        src="/wechat-qrcode.png" 
                        alt="微信收款码"
                        style={{ 
                          width: '200px', 
                          height: '200px', 
                          borderRadius: '12px',
                          objectFit: 'cover',
                          border: '2px solid #07c160'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="qrcode-placeholder wechat" style={{ display: 'none' }}>
                        <div className="qrcode-icon">🔲</div>
                        <p>微信收款码</p>
                        <p className="qrcode-tip">请放置 wechat-qrcode.png</p>
                      </div>
                    </div>
                    <p className="payment-tip">使用微信扫一扫上方二维码完成支付</p>
                  </>
                )}
              </div>
            </div>

            {/* 订单信息 */}
            {showOrderInfo && currentOrder && (
              <div className="order-info-box" style={{
                background: '#fff3e0',
                border: '2px solid #ff9800',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#e65100', fontSize: '16px' }}>
                  📋 订单信息
                </h4>
                <p style={{ margin: '4px 0', fontSize: '14px' }}>
                  <strong>订单号：</strong>{currentOrder.orderId}
                </p>
                <p style={{ margin: '4px 0', fontSize: '14px' }}>
                  <strong>套餐：</strong>{selectedPlan?.name}
                </p>
                <p style={{ margin: '4px 0', fontSize: '14px' }}>
                  <strong>金额：</strong>¥{selectedPlan?.price}
                </p>
                <div style={{ 
                  background: 'white', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  marginTop: '12px',
                  border: '2px dashed #ff9800'
                }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold', color: '#d84315' }}>
                    ⚠️ 支付时请务必在备注中填写：
                  </p>
                  <p style={{ 
                    margin: '0', 
                    fontSize: '18px', 
                    fontWeight: 'bold', 
                    color: '#e65100',
                    fontFamily: 'monospace',
                    letterSpacing: '1px'
                  }}>
                    {currentOrder.note}
                  </p>
                  <button 
                    className="btn btn-outline"
                    style={{ marginTop: '8px', fontSize: '12px', padding: '4px 8px' }}
                    onClick={() => {
                      navigator.clipboard.writeText(currentOrder.note);
                      alert('备注信息已复制到剪贴板');
                    }}
                  >
                    📋 复制备注
                  </button>
                </div>
                <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: '#666' }}>
                  💡 管理员核对备注后会为您激活订阅
                </p>
              </div>
            )}

            <div className="payment-actions">
              {!showOrderInfo ? (
                <>
                  <button 
                    className="btn btn-outline" 
                    onClick={() => setShowPaymentModal(false)}
                  >
                    取消
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={handleCreateOrder}
                  >
                    📝 确认并生成订单
                  </button>
                </>
              ) : (
                <>
                  <button 
                    className="btn btn-outline" 
                    onClick={() => {
                      setShowPaymentModal(false);
                      setShowOrderInfo(false);
                      setCurrentOrder(null);
                    }}
                  >
                    关闭
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={handlePaymentSuccess}
                    style={{ background: '#4caf50' }}
                  >
                    ✅ 我已完成支付
                  </button>
                </>
              )}
            </div>

            <p className="payment-note">
              {!showOrderInfo 
                ? '点击"确认并生成订单"后，系统会生成唯一订单号和支付备注' 
                : '支付完成后请点击"我已完成支付"，管理员核对后会激活您的订阅'}
            </p>
          </div>
        </div>
      )}

      {/* 开发测试功能 - 仅开发环境显示 */}
      {process.env.NODE_ENV === 'development' && (
        <div className="dev-test-panel" style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: 'white',
          padding: '16px',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          zIndex: 999,
          maxWidth: '300px'
        }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>
            🧪 开发测试工具
          </h4>
          <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#999' }}>
            模拟用户订阅流程，测试支付二维码切换
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button 
              className="btn btn-primary"
              style={{ fontSize: '13px', padding: '8px 12px' }}
              onClick={() => {
                const testPlan = plans.find(p => p.id === 'monthly');
                setSelectedPlan(testPlan);
                setShowPaymentModal(true);
              }}
            >
              测试月度会员(¥29)
            </button>
            <button 
              className="btn btn-primary"
              style={{ fontSize: '13px', padding: '8px 12px', background: '#ff9800' }}
              onClick={() => {
                const testPlan = plans.find(p => p.id === 'quarterly');
                setSelectedPlan(testPlan);
                setShowPaymentModal(true);
              }}
            >
              测试季度会员(¥79)
            </button>
            <button 
              className="btn btn-primary"
              style={{ fontSize: '13px', padding: '8px 12px', background: '#9c27b0' }}
              onClick={() => {
                const testPlan = plans.find(p => p.id === 'yearly');
                setSelectedPlan(testPlan);
                setShowPaymentModal(true);
              }}
            >
              测试年度会员(¥239)
            </button>
            <button 
              className="btn btn-outline"
              style={{ fontSize: '12px', padding: '6px 10px' }}
              onClick={() => {
                localStorage.removeItem('subscription');
                setSubscription(null);
                alert('✅ 已清除订阅状态');
              }}
            >
              清除订阅状态
            </button>
          </div>
        </div>
      )}

      {/* 主内容区 */}
      <div className="subscription-content">
        {/* 当前订阅状态 */}
        {subscription && (
          <div className="current-subscription">
            <div className="subscription-status active">
              <span className="status-badge">✓ 已订阅</span>
              <h2>{subscription.planName}</h2>
              <p>有效期至：{formatDate(subscription.expiryDate)}</p>
              <p className="days-remaining">剩余 <span>{getDaysRemaining()}</span> 天</p>
            </div>
            <button 
              className="btn btn-outline" 
              onClick={() => {
                localStorage.removeItem('subscription');
                setSubscription(null);
              }}
            >
              取消订阅
            </button>
          </div>
        )}

        {/* 订阅标题区 */}
        <div className="subscription-header">
          <h1>✈️ 选择您的学习计划</h1>
          <p>基于上海高考英语标准，五维一体精读训练体系</p>
        </div>

        {/* 订阅方案 */}
        <div className="pricing-grid">
          {plans.map(plan => (
            <div 
              key={plan.id} 
              className={`pricing-card ${plan.popular ? 'popular' : ''}`}
              style={{ '--plan-color': plan.color }}
            >
              {plan.popular && (
                <div className="popular-badge">🔥 最受欢迎</div>
              )}
              
              <div className="pricing-header" style={{ backgroundColor: plan.color }}>
                <h3>{plan.name}</h3>
                <div className="price">
                  {plan.price === 0 ? (
                    <span className="price-amount">免费</span>
                  ) : (
                    <>
                      <span className="price-currency">¥</span>
                      <span className="price-amount">{plan.price}</span>
                      <span className="price-period">/{plan.period}</span>
                    </>
                  )}
                </div>
                {plan.originalPrice && (
                  <span className="original-price">¥{plan.originalPrice}</span>
                )}
                <p className="plan-desc">{plan.description}</p>
              </div>

              <div className="pricing-features">
                {plan.features.map((feature, idx) => (
                  <div key={idx} className="feature-item">
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <button 
                className="btn btn-primary btn-block"
                onClick={() => handleSubscribe(plan.id)}
                disabled={subscription?.planId === plan.id}
              >
                {subscription?.planId === plan.id ? '✓ 已订阅' : '立即订阅'}
              </button>
            </div>
          ))}
        </div>

        {/* 会员权益说明 */}
        <div className="benefits-section">
          <h2>🎁 会员专属权益</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">📚</div>
              <h3>海量精选内容</h3>
              <p>50+精选外刊文章，每周更新</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🤖</div>
              <h3>AI智能批改</h3>
              <p>基于大语言模型的精准评分与反馈</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">📊</div>
              <h3>学习数据分析</h3>
              <p>全方位追踪学习进度与成果</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">👨‍🏫</div>
              <h3>专属学习顾问</h3>
              <p>专业老师一对一指导</p>
            </div>
          </div>
        </div>
      </div>

      {/* 底部信息 */}
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>✈️ 智伴英航教育</h4>
            <p>专注于K12英语教育，提供全方位的学习解决方案</p>
          </div>
          <div className="footer-section">
            <h4>📞 联系方式</h4>
            <p>电话：187-8306-7878</p>
            <p>邮箱：aenedu.site@gmail.com</p>
            <p>地址：上海市 · 松江区 · 九亭镇</p>
          </div>
          <div className="footer-section">
            <h4>🔗 快速链接</h4>
            <Link to="/">首页</Link>
            <Link to="/subscription">订阅中心</Link>
            <Link to="/manager">文章管理</Link>
          </div>
        </div>
        <div className="footer-copyright">
          © 2026 智伴英航教育科技有限公司 版权所有
        </div>
      </footer>
    </div>
  );
}